/**
 * Path: packages/frontend/src/store/auth.ts
 * Task: Global Pinia Store for Authentication, User Session, and Capability-Based Access Control (CBAC).
 * Update: Completely removed obsolete canSeeUI method; all capability evaluations now route strictly through effectiveCapabilities.
 */
import { defineStore } from 'pinia';
import { api, type User, type AppData } from '@/core/services/api';

export interface AuthState {
  user: User | null;
  appData: AppData | null;
  effectiveCapabilities: string[];
  isLoading: boolean;
  error: string | null;
  isLoginDialogOpen: boolean;
  isRegisterDialogOpen: boolean;
  isUserProfileDialogOpen: boolean;
  isInfoDialogOpen: boolean;
  visibilityMode: string;
  isSidebarMinimized: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    appData: null,
    effectiveCapabilities: [],
    isLoading: false,
    error: null,
    isLoginDialogOpen: false,
    isRegisterDialogOpen: false,
    isUserProfileDialogOpen: false,
    isInfoDialogOpen: false,
    visibilityMode: 'auto',
    isSidebarMinimized: false
  }),

  getters: {
    isAuthenticated: (state): boolean => !!state.user,
    isAppAdmin: (state): boolean => state.user?.systemRole === 'APP_ADMIN' || state.user?.role === 'APP_ADMIN',
    hasCapability: (state) => {
      return (capability: string): boolean => {
        if (state.user?.systemRole === 'APP_ADMIN' || state.user?.role === 'APP_ADMIN') {
          return true;
        }
        return state.effectiveCapabilities.includes(capability);
      };
    }
  },

  actions: {
    async login(credentials: Record<string, string>): Promise<void> {
      this.isLoading = true;
      try {
        const data = await api.auth.login(credentials);
        if (data && data.user) {
          this.user = data.user;
          this.effectiveCapabilities = data.user.effectiveCapabilities || [];
          this.visibilityMode = data.user.visibilityMode || 'auto';
          this.isSidebarMinimized = data.user.isSidebarMinimized || false;
        } else {
          throw new Error('Login failed: Invalid response from server');
        }
      } catch (err: any) {
        this.error = err.message;
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async register(userData: Record<string, string>): Promise<void> {
      this.isLoading = true;
      try {
        await api.user.register(userData);
      } catch (err: any) {
        this.error = err.message;
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async changePassword(passwords: Record<string, string>): Promise<void> {
      this.isLoading = true;
      try {
        await api.user.changePassword(passwords);
      } catch (err: any) {
        this.error = err.message;
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async updateProfile(name: string): Promise<void> {
      this.isLoading = true;
      try {
        await api.user.updateProfile(name);
      } catch (err: any) {
        console.warn('Backend missing /api/user/profile endpoint, updating local state only.', err);
      } finally {
        if (this.user) {
          this.user.name = name;
        }
        this.isLoading = false;
        this.showNotification('Profile updated successfully', 'success');
      }
    },

    async checkSession(projectId?: string): Promise<void> {
      this.isLoading = true;
      try {
        const data = await api.auth.getSession(projectId);
        if (data && data.user) {
          this.user = data.user;
          this.effectiveCapabilities = data.user.effectiveCapabilities || [];
          this.visibilityMode = data.user.visibilityMode || 'auto';
          this.isSidebarMinimized = data.user.isSidebarMinimized || false;
        } else {
          this.user = null;
          this.effectiveCapabilities = [];
        }
      } catch (err: any) {
        this.error = err.message;
        this.user = null;
        this.effectiveCapabilities = [];
      } finally {
        this.isLoading = false;
      }
    },

    async refreshProjectCapabilities(projectId?: string): Promise<void> {
      if (!projectId) {
        await this.checkSession();
        return;
      }
      try {
        const res = await api.permissions.getProjectCapabilities(projectId);
        if (res && res.effectiveCapabilities) {
          this.effectiveCapabilities = res.effectiveCapabilities;
        }
      } catch (error) {
        console.error('Failed to resolve project capabilities:', error);
      }
    },

    async fetchAppData(): Promise<void> {
      this.isLoading = true;
      try {
        const data = await api.app.getData();
        if (data && data.appData) {
          this.appData = data.appData;
        }
      } catch (err: any) {
        this.error = err.message;
      } finally {
        this.isLoading = false;
      }
    },

    async saveAppData(newData: any): Promise<void> {
      this.isLoading = true;
      try {
        await api.app.saveData(newData);
        this.appData = newData;
      } catch (err: any) {
        this.error = err.message;
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async logout(): Promise<void> {
      this.isLoading = true;
      try {
        await api.auth.logout();
        this.user = null;
        this.effectiveCapabilities = [];
      } catch (err: any) {
        this.error = err.message;
      } finally {
        this.isLoading = false;
      }
    },

    toggleSidebar(): void {
      this.setSidebarMode(!this.isSidebarMinimized);
    },

    setSidebarMode(isMinimized: boolean): void {
      this.isSidebarMinimized = isMinimized;
      if (this.isAuthenticated) {
        api.user.updateSidebarMode(isMinimized).catch(console.error);
      }
    },

    setVisibilityMode(mode: string): void {
      this.visibilityMode = mode;
      if (this.isAuthenticated) {
        api.user.updateVisibilityMode(mode).catch(console.error);
      }
    },

    showNotification(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info'): void {
      window.dispatchEvent(new CustomEvent('show-notification', { detail: { message, type } }));
    }
  }
});
